import { X, Sparkles } from 'lucide-react';

interface ChangelogModalProps {
  onClose: () => void;
}

interface IChangelogEntry {
  version: string;
  title: string;
  items: string[];
}

const CHANGELOG: IChangelogEntry[] = [
  {
    version: 'v3.28.0',
    title: 'Test Cases — Folders, IDs, Severity & Tags',
    items: [
      'Test Cases now organize into a folder tree — create nested folders by feature, epic, or sprint and drag tests between them',
      'Every test case gets a human-readable ID (AG-TC-001, AG-TC-002…) for easy cross-team reference in tickets and reviews',
      'New Severity field (Critical / High / Medium / Low) and Case Type (Functional / Regression / Smoke / Performance / Security / Usability) for richer classification',
      'Add free-form tags to test cases for cross-cutting concerns like "login", "payment", or "mobile"',
      'New full-page detail view with dedicated tabs for Steps, Comments, Change History, and Details',
      'Quick-add bar in the list view lets you create a new test case without opening a separate modal',
      'Test Cases URLs are now fully shareable — deep link directly to a folder, open test case, active tab, or fullscreen view',
    ],
  },
  {
    version: 'v3.27.0',
    title: 'Search & Filter — Test Cycles and Test Cases',
    items: [
      'Both Test Cycles and Test Cases now have a live search bar: type to instantly filter by cycle name or test case title — results update after a 350ms debounce so the API is never spammed',
      'Test Cycles: new Status dropdown (All / Pending / Running / Completed) lets you drill into exactly the cycle state you care about without scrolling through the full list',
      'Test Cases: new Type dropdown (All / Manual / Automated) lets you isolate manual or automated cases across all suites in one click',
      'All filter state is URL-synced (?search=, ?status=, ?type=) — filtered views can be bookmarked, shared with teammates, or deep-linked directly to a specific result set',
      'Switching project via the header selector no longer resets your active search or filter — all URL params are preserved independently',
    ],
  },
  {
    version: 'v3.26.0',
    title: 'Test Cycles — Manual vs CI Tab Separation',
    items: [
      'Test Cycles now has two tabs: "Manual Cycles" (default) and "CI / Automated" — machine-generated CI runs no longer mix with human-created cycles',
      'CI cycles from GitHub, GitLab, webhooks, and PR routing automatically land on the CI tab — no manual tagging required',
      '"+ Create Cycle" button is only visible on the Manual tab, keeping the CI tab read-only and clutter-free',
      'CI cycle rows now show branch name, repository, and PR number as subtle metadata under the cycle name',
    ],
  },
  {
    version: 'v3.25.1',
    title: 'DocSync — Automation Planner & Runtime Context Docs',
    items: [
      'Automation Planner is now correctly listed as the seventh AI feature in the Getting Started intro',
      'Navigation sidebar entry for Automation Planner added to the Getting Started documentation',
      'README Project Status table updated — Phase 8.1 Framework-Agnostic Runtime Context (v3.25.0) row added',
      'AI Providers description now enumerates all capabilities including Spec-to-Test and Automation Planning',
    ],
  },
  {
    version: 'v3.25.0',
    title: 'Phase 8.1 — Framework-Agnostic Runtime Context',
    items: [
      '@agnox/playwright-reporter upgraded to v2.0.0. The reporter now exports a drop-in test fixture — replace your @playwright/test import in one shared fixtures file and zero individual test files are touched.',
      'New auto-fixture _agnoxContextCapture ({ auto: true }) captures structured browser context at the exact moment of failure: page URL, page title, console errors, failed network requests (4xx/5xx + connection failures), lightweight ARIA DOM snapshot, viewport size, and retry index.',
      'Structured context is attached as an agnox:runtimeContext annotation and delivered on the test-end ingest event — the AI root-cause analysis now consumes typed, structured data instead of grepping for a magic [Agnox Context] string in 30k characters of logs.',
      'Fully backwards-compatible: v1.x playwright.config.ts configurations continue to work unchanged. Structured context is opt-in via the one-line fixture re-export.',
      'Phases 8.2 (Cypress plugin) and 8.3 (Pytest plugin) are on the roadmap to bring the same zero-test-file-change experience to all major frameworks.',
    ],
  },
  {
    version: 'v3.24.0',
    title: 'Phase 6 — AI Automation Planner',
    items: [
      'New Automation Planner page (/automation-planner): AI scores every manual test as CRITICAL, HIGH, MEDIUM, or LOW risk and groups them by suite — giving you an instant prioritised automation roadmap.',
      '2-Stage scan architecture: Stage 1 sends only test metadata for fast risk stratification; Stage 2 deep-fetches full step data only for the tests you select (max 30) — keeping token costs predictable regardless of library size.',
      'Targeted Discovery: filter the scan by Suite or Tag before it runs to focus on one area at a time without re-scanning your entire project.',
      'IDE Code Artifacts: clicking "Generate Plan" produces a complete 6-section Markdown document (Executive Summary, Prerequisites, Folder Structure, Coverage Map, Implementation Notes, Master AI Coding Prompt) ready to paste directly into Cursor or GitHub Copilot.',
      'Plan History: every generated plan is saved to the automationPlans collection and instantly re-openable from the History panel — no LLM call required to revisit a previous artifact.',
      'Toggle the feature under Settings → Features → AI Features → Automation Planner.',
    ],
  },
  {
    version: 'v3.23.0',
    title: 'DocSync — Docs Audit & Free Tier Corrections',
    items: [
      'Full documentation sync across all architecture, API reference, and integration guides to accurately reflect the multi-provider BYOK AI stack, Global Project Selector, 5-tier billing, and all connectors shipped through v3.22.0.',
      'Free plan limits corrected in API docs: maxTestRuns updated from 100 to 50; maxConcurrentRuns and maxStorageBytes fields added to the limits object example.',
      'README updated: billing description expanded from 3 tiers to 5; Spec-to-Test Generation added to the AI Quality Orchestrator capability list; project status table extended with v3.19.0–v3.22.0 milestones.',
      'Stale "Phase" labels, hardcoded model references, and legacy diagram nodes purged — documentation now reflects production reality without any sprint-era placeholders.',
    ],
  },
  {
    version: 'v3.22.0',
    title: 'Spec-to-Test AI Generation & 5-Tier Billing',
    items: [
      'AI Spec-to-Test Generation (Feature F): upload a PDF, DOCX, or Markdown spec file and watch a 4-stage agentic pipeline (Extractor → Generator → Critic → Formatter) stream AI-generated test cases live via SSE. Duplicate detection uses MongoDB $text search + LLM grounding. Import up to 50 cases at a time from the 3-step wizard in Test Cases.',
      '5-Tier Billing Model: plans expanded from 3 to 5 tiers — Free (50 runs), Starter ($29 / 250 runs), Team ($99 / 1 000 runs), Business ($299 / 2 500 runs), and Enterprise (custom / unlimited). Canonical limits are enforced from a single plans.ts source of truth, overriding any stale DB values.',
      'Global Project Selector: a platform-wide project dropdown in the header (and sidebar on mobile) persists your active project in localStorage and synchronises all pages, URL params, and data hooks simultaneously.',
      'Storage Tracking: the worker now atomically increments storage bytes on each execution finish; a nightly reconciler (02:00 UTC) recalculates true usage via MongoDB $bsonSize and corrects any drift.',
      'Bulk & Suite deletion for test cases with an amber warning when AUTOMATED tests are selected; Test Cycle hard-delete with a 409 guard when a cycle is still RUNNING.',
      'Settings "Security" tab renamed to "AI Models" for clarity.',
    ],
  },
  {
    version: 'v3.21.0',
    title: 'Monday.com & Generic Webhook Integrations',
    items: [
      'Monday.com integration: create board items directly from the Investigation Hub or Auto-Bug Generator. Item ID and URL are written back to the execution for bidirectional tracking.',
      '"Submit to Monday.com" button added to the Auto-Bug Generator alongside Jira and Linear.',
      'Generic outbound webhook: configure any HTTPS endpoint to receive a signed execution.finished event whenever a run reaches a terminal state. Optional HMAC-SHA256 x-agnox-signature header when a secret is set.',
      'In-app changelog modal now surfaces the latest 5 platform releases with version badges and feature summaries.',
    ],
  },
  {
    version: 'v3.20.0',
    title: 'Linear Integration',
    items: [
      'Full bidirectional Linear issue integration across the platform.',
      'Create Linear issues directly from the Investigation Hub drawer, with the resulting issue ID and URL written back to the execution.',
      'Linear settings card added to Settings → Connectors to securely store your API key and Team ID.',
      '"Submit to Linear" button added to the Auto-Bug Generator alongside Jira.',
    ],
  },
  {
    version: 'v3.19.0',
    title: 'Bitbucket CI Integration',
    items: [
      'Native Bitbucket CI provider: the worker now posts AI Analysis reports as Pull Request comments on Bitbucket Cloud, completing the four-provider CI suite (GitHub, GitLab, Azure DevOps, Bitbucket).',
      'Bitbucket PAT management card added to Settings → Integrations: paste your token, save, and the integration is immediately active for future PR runs.',
      'Bitbucket source union added to shared-types: executions and ingest sessions that originate from Bitbucket Pipelines are correctly typed and attributed.',
    ],
  },
  {
    version: 'v3.18.0',
    title: 'MS Teams Notifications',
    items: [
      'New Microsoft Teams integration: configure an Incoming Webhook in Settings → Integrations to receive colour-coded MessageCard notifications whenever a test execution reaches a final status.',
      'Each status (PASSED, FAILED, ERROR, UNSTABLE) is individually toggleable — Teams and Slack channels are completely independent; a failure in one never suppresses the other.',
      'Failed/Error executions include a truncated AI analysis snippet and a deep link to the Investigation Hub directly in the Teams message.',
      'Webhook URL is validated against the official *.webhook.office.com/webhookb2/ domain (SSRF protection) and stored AES-256-GCM encrypted.',
    ],
  },
  {
    version: 'v3.17.1',
    title: 'Documentation Sync — Smart PR Badge & Multi-Provider AI',
    items: [
      'Smart PR Badge and AI Reasoning Audit Trail are now fully documented: every PR-dispatched execution carries isSmartPR: true and an aiReasoning field surfaced in the Investigation Hub.',
      'groupName default documented: PR webhook runs automatically join the "Smart-PR-Run" group when ?groupName= is omitted.',
      'AI provider badge, architecture diagram, and component table updated to accurately reflect multi-provider BYOK support (Gemini, GPT-4o, Claude).',
    ],
  },
  {
    version: 'v3.17.0',
    title: 'Smart PR Badge, AI Reasoning Audit Trail & Grouping Defaults',
    items: [
      'Smart PR Badge: every execution dispatched by the PR webhook now carries an isSmartPR flag, rendering a distinct Smart PR badge in the Execution List for at-a-glance identification.',
      'AI Reasoning Audit Trail: the LLM\'s folder-selection rationale (aiReasoning) is persisted on the execution record and surfaced in the Investigation Hub so teams can audit every routing decision.',
      'groupName Default: PR webhook runs automatically join the "Smart-PR-Run" group when ?groupName= is omitted, ensuring Auto-Quarantine and Flakiness Detective always receive the historical data they need.',
      'Webhook URL callout in Run Settings now includes ?env=prod in the example, clarifying environment-pinning usage for new integrators.',
    ],
  },
  {
    version: 'v3.15.0',
    title: 'Smart PR Webhooks (AI-Driven Routing)',
    items: [
      'Robust CI context extraction from GitHub webhooks now supports both pull_request and push event formats — branch, commit SHA, and PR/commit URL are always displayed in the Execution Drawer.',
      'Test Manifest Seeding: PR Routing LLM prompt dynamically incorporates your project\'s test structure manifest for precise file-to-test mapping.',
      'New ?env=prod|staging|dev query parameter on the PR Routing webhook lets CI callers pin the target environment explicitly.',
    ],
  },
  {
    version: 'v3.14.0',
    title: 'AI Model Artifact Persistence & Pipeline Hardening',
    items: [
      'AI Model Artifact Persistence: the specific AI model used for each analysis is now immutably recorded on the Execution object for strict provenance tracking.',
      'AI Analysis Pipeline Hardening: fixed a technical error in the AI analysis route and added allure-playwright fallback support for improved report generation.',
      'Comprehensive regression review across 22 files with 4 specific bug fixes.',
    ],
  },
  {
    version: 'v3.13.0',
    title: 'Smart Execution Analytics',
    items: [
      'Persistent per-test health metrics: stabilityScore (A–F grade), averageDurationMs, and isQuarantined state tracked across all executions.',
      'Auto-Quarantine & Quality Gate Bypass: tests failing 3 consecutive times are automatically quarantined — CI/CD webhooks return PASSED when all failures are quarantined.',
      'AI Failure Clustering: MD5-based errorHash groups similar failures to optimize AI token usage and analysis accuracy.',
      '🐌 Performance Degradation detection flags tests running significantly slower than their historical average.',
    ],
  },
  {
    version: 'v3.12.0',
    title: 'Security Hardening & Docusaurus Docs',
    items: [
      'SAST audit resolved: all Critical and High severity findings fixed — security score advanced to 100/100.',
      'Webhook HMAC signature validation: PR Routing endpoint now enforces X-Hub-Signature-256 with constant-time comparison; tampered deliveries rejected 401.',
      'Synchronous startup guard: Producer service validates ENCRYPTION_KEY, PLATFORM_JWT_SECRET, and PLATFORM_MONGO_URI at boot — refuses to start on any missing secret.',
      '5-Layer AI Pipeline Sanitizer formally documented: stage allowlist, forced organizationId injection, $limit cap (max 1,000), collection whitelist, recursive operator scan.',
      'Documentation restructured into Docusaurus with dedicated sections for getting-started, core-features, ai-capabilities, integrations, api-reference, and architecture.',
    ],
  },
  {
    version: 'v3.11.0',
    title: 'AI Feature Documentation Deep-Dive',
    items: [
      'BYOK Configuration guide updated: step-by-step setup in Settings → Security → AI Configuration with per-provider key management and AES-256-GCM encryption guarantee.',
      'Auto-Bug Generator: documented log-truncation strategy (first 10% + last 90%, max 80k chars) and full field breakdown.',
      'Smart PR Routing: added GitLab webhook setup instructions alongside GitHub steps.',
      'Quality Chatbot: supported chart types table, 5-column security guard table, and 24h conversation TTL documented.',
      'New Dual-Agent (Actor-Critic) Architecture subsection with ASCII pipeline diagram and temperature/schema enforcement details.',
    ],
  },
  {
    version: 'v3.10.0',
    title: 'AI Quality Orchestrator',
    items: [
      'Feature A — Auto-Bug Generator: "Auto Bug" button in the Investigation Hub generates a structured bug report (title, steps to reproduce, expected/actual behavior, code patch suggestions) from execution logs. Edit before submitting directly to Jira.',
      'Feature B — Flakiness Detective: New Stability page (/stability) analyzes a test group\'s last 20 executions to produce a flakiness score, verdict, findings, and recommendations. Full history persisted and clickable.',
      'Feature C — Test Case Optimizer: Select test cases in the Test Cases page and click "Optimize with AI" to get a dual-agent BDD optimization with duplicate detection and edge-case suggestions. Side-by-side diff view before applying.',
      'Feature D — Smart PR Routing: GitHub push webhooks trigger automatic test selection — the LLM maps changed files to the appropriate test folder and dispatches the run via RabbitMQ. Configure in Settings → Run Settings.',
      'Feature E — Quality Chatbot: New Ask AI page (/chat) answers natural-language questions about your test data using a two-turn LLM pipeline with a 5-layer NoSQL injection sanitizer and optional chart responses.',
      'BYOK Support: Settings → Security now lets admins set the org\'s default AI model (Gemini, GPT-4o, Claude) and store per-provider API keys encrypted at rest. AI features are individually toggled in Settings → Features.',
    ],
  },
  {
    version: 'v3.9.0',
    title: 'Onboarding Widget & Guided Tours',
    items: [
      'New getting-started checklist widget (bottom-right) guides new users through three key actions: connecting a Docker image, running their first test, and exploring platform features.',
      '11-step guided tour (buildEmptyStateTour) walks through Run Settings configuration, first execution launch, and Investigation Hub walkthrough.',
      '5-step platform discovery tour (buildFeatureTour) spotlights Test Cases, Test Cycles, Team Members, and Env Variables via sidebar navigation.',
      'Sidebar "Getting Started" recovery button re-opens a dismissed onboarding widget without a page reload.',
      'Completed checklist items show a "Replay" badge and remain clickable for revisiting any tour.',
    ],
  },
  {
    version: 'v3.8.1',
    title: 'Reporter Troubleshooting & Dual Architecture Docs',
    items: [
      'Added Troubleshooting / FAQ to the @agnox/playwright-reporter README covering the three most common setup pitfalls.',
      'Integration Quickstart now explains the Agnox Hosted vs. External CI dual architecture with a side-by-side comparison table.',
      'CI/CD guide updated with a dedicated reporter section including GitHub Actions workflow and a troubleshooting table.',
    ],
  },
  {
    version: 'v3.8.0',
    title: 'Native Playwright Reporter',
    items: [
      'New @agnox/playwright-reporter package streams live Playwright test results directly to your Agnox dashboard in real-time.',
      'Auto-detects CI environment (GitHub Actions, GitLab CI, Azure DevOps, Jenkins) and attaches CI context to every run.',
      'New "Source" filter on the Dashboard separates Agnox-hosted Docker runs from External CI passive runs.',
    ],
  },
  {
    version: 'v3.7.0',
    title: 'CI/CD API Key Authentication',
    items: [
      'The /api/ci/trigger endpoint now accepts x-api-key header authentication — CI pipelines no longer need a user Bearer token.',
      'Project ID field added to Run Settings with a one-click Copy button for use in CI/CD workflows.',
    ],
  },
  {
    version: 'v3.6.0',
    title: 'Dual-Agent AI Analysis Pipeline',
    items: [
      'AI root cause analysis now uses a two-step Analyzer + Critic pipeline — the Critic validates the initial diagnosis against raw logs and eliminates hallucinations before output.',
      'Log context passed to Gemini expanded to 60 000 characters, capturing full suite output and earlier setup failures.',
      'Analysis accuracy improvements: Analyzer uses structured JSON schema; Critic runs at temperature 0.0 for factual grounding.',
    ],
  },
  {
    version: 'v3.5.0',
    title: 'DocSync & Multi-Tenant Architecture Alignment',
    items: [
      'Synchronized all project documentation to reflect the new Multi-Tenant architecture.',
      'Removed legacy environment variables and decoupled platform from client test workloads.',
      'Documented background Docker image pre-fetching mechanism for execution wait times.',
    ],
  },
  {
    version: 'v3.4.0',
    title: 'Env Variables & Secrets Management',
    items: [
      'Added per-project environment variables with AES-256-GCM encryption.',
      'Execution pipeline integration securely injects env vars into test runs.',
      'New Environment Variables Settings tab with masked table view and secret toggles.',
    ],
  },
  {
    version: 'v3.3.0',
    title: 'Slack Notifications & Execution Polishes',
    items: [
      'Configurable Slack Notifications based on test execution statuses.',
      'Added "Connected" status badges for CI integrations.',
      'Global Brand Refresh to "Agnox" and various UI/UX bug fixes.',
    ],
  },
  {
    version: 'v3.2.0',
    title: 'Native CI/CD Integrations',
    items: [
      'Native CI Provider implementations to post AI root-cause analysis as PR/MR comments.',
      'Dynamic API Routing and encrypted storage for integration credentials (PATs).',
      'New Provider Settings UI in the Dashboard to securely manage tokens.',
    ],
  },
  {
    version: 'v3.1.0',
    title: 'Quality Hub & Reporting Evolution',
    items: [
      'Live HTML Reports: Dedicated preview screen for Test Cycles with native browser-print optimization.',
      'Feature Management: Organization-level toggles for Manual Test Repository and Hybrid Cycles.',
      'Automated Versioning: Single-source-of-truth versioning injected from package.json to the entire UI.',
    ],
  },
];

export function ChangelogModal({ onClose }: ChangelogModalProps) {
  return (
    <div
      role="presentation"
      className="fixed inset-0 z-[1000] flex items-center justify-center p-5 bg-black/50"
      onClick={onClose}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="changelog-modal-title"
        className="bg-white dark:bg-gh-bg-dark rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-auto border border-slate-200 dark:border-gh-border-dark"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200 dark:border-gh-border-dark">
          <div className="flex items-center gap-2.5">
            <Sparkles size={18} className="text-blue-500 dark:text-blue-400" />
            <h2
              id="changelog-modal-title"
              className="text-lg font-semibold text-slate-900 dark:text-slate-100"
            >
              What's New in agnox
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close changelog"
            className="p-1.5 rounded-lg text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-gh-bg-subtle-dark transition-colors cursor-pointer"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 flex flex-col gap-6">
          {CHANGELOG.slice(0, 5).map((entry) => (
            <div key={entry.version}>
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
                  {entry.version}
                </span>
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  {entry.title}
                </span>
              </div>
              <ul className="flex flex-col gap-2">
                {entry.items.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-slate-700 dark:text-slate-300">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-gh-border-dark">
          <a
            href="https://github.com/agnox/agnox/releases"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-slate-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            See all releases →
          </a>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 bg-white dark:bg-gh-bg-subtle-dark border border-slate-200 dark:border-gh-border-dark rounded-lg hover:bg-slate-50 dark:hover:bg-gh-bg-dark transition-colors cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
